import { ReactNode } from 'react'
import { Control, FieldPath, FieldValues } from 'react-hook-form'

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { addPrefixToObject, removePrefix } from '@/utils/prefixer'

import { Label } from './label'

// prettier-ignore
export interface SwitchProps<
  TFieldValues extends FieldValues             = FieldValues,
  TName        extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends
  addPrefixToObject<React.HTMLAttributes<HTMLDivElement>,          'wrapper_'>,
  addPrefixToObject<React.ButtonHTMLAttributes<HTMLButtonElement>, 'switch_'>
{
  field_control:      Control<TFieldValues>
  field_name:         TName
  field_label?:       ReactNode
  field_description?: ReactNode
}

const Component = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  field_control,
  field_name,
  field_label,
  field_description,
  ...otherProps
}: SwitchProps<TFieldValues, TName>) => {
  // prettier-ignore
  const prefixedProps = {
    wrapper: removePrefix<React.HTMLAttributes<HTMLDivElement>>          (otherProps, 'wrapper_'),
    switch:  removePrefix<React.ButtonHTMLAttributes<HTMLButtonElement>> (otherProps, 'switch_'),
  }

  return (
    <FormField
      control={field_control}
      name={field_name}
      render={({ field: { value, onChange } }) => (
        <FormItem
          {...prefixedProps.wrapper}
          className={cn(
            'flex items-center justify-between gap-8 rounded-md border border-muted-foreground/20 p-2',
            prefixedProps.wrapper.className,
          )}
        >
          <div className="space-y-0.5">
            <Label>{field_label}</Label>
            {field_description && (
              <FormDescription>{field_description}</FormDescription>
            )}
          </div>
          <FormControl>
            <Switch
              checked={value}
              onCheckedChange={onChange}
              {...prefixedProps.switch}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
Component.displayName = 'Switch'

export { Component as Switch }
